import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Typography, Button, Alert, Chip, Card, CardContent, CardActions,
    IconButton, Tooltip, CircularProgress, LinearProgress, Paper,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
    Add, Edit, Delete, SwapHoriz, Receipt, AccountBalance,
    TrendingUp, TrendingDown, WalletOutlined, CreditCard, History,
} from "@mui/icons-material";
import { financialAccountService } from "../../services/financialAccountService";
import { invalidateAccountsCache } from "../../hooks/useAccounts";
import { AccountForm } from "../../components/accounts/AccountForm";
import { TransferDialog } from "../../components/accounts/TransferDialog";
import { AccountStatementDrawer } from "../../components/accounts/AccountStatementDrawer";
import { TransferHistoryDrawer } from "../../components/accounts/TransferHistoryDrawer";
import { ACCOUNT_TYPE_META } from "../../components/accounts/accountTypeMeta";
import { FinancialAccount, FinancialAccountWithBalance, AccountType } from "../../types/accounts";
import { formatCurrencyBDT } from "../../utils/currency";
import { usePermissions } from "../../hooks/usePermissions";

// Non-loan types render together, ordered most-liquid-first; LOAN gets its
// own section below (see NON_LOAN_ORDER usage in sortAccounts).
const NON_LOAN_ORDER: AccountType[] = ["CASH", "BANK", "MOBILE_WALLET", "OTHER"];

function sortAccounts(accounts: FinancialAccountWithBalance[]): FinancialAccountWithBalance[] {
    return [...accounts].sort((a, b) => {
        const rank = NON_LOAN_ORDER.indexOf(a.type) - NON_LOAN_ORDER.indexOf(b.type);
        return rank !== 0 ? rank : a.name.localeCompare(b.name);
    });
}

function daysUntil(dateStr: string): number {
    const target = new Date(`${dateStr}T00:00:00`).getTime();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((target - today.getTime()) / 86400000);
}

/** A due-date pill for LOAN accounts — the closer/more overdue, the louder
 * the color, so a repayment that needs attention doesn't just blend into
 * a wall of identical caption text. */
const RepaymentChip: React.FC<{ date: string }> = ({ date }) => {
    const days = daysUntil(date);
    if (days < 0) return <Chip size="small" color="error" label={`Overdue ${Math.abs(days)}d`} />;
    if (days === 0) return <Chip size="small" color="error" label="Due today" />;
    if (days <= 14) return <Chip size="small" color="warning" label={`Due in ${days}d`} />;
    return <Chip size="small" variant="outlined" label={`Due ${date}`} />;
};

// ── KPI card ──────────────────────────────────────────────────────────────────

interface KpiProps { label: string; value: string; sub?: string; icon: React.ReactNode; color: string }

const KpiCard: React.FC<KpiProps> = ({ label, value, sub, icon, color }) => (
    <Card variant="outlined" sx={{ height: "100%" }}>
        <CardContent sx={{ pb: "16px !important" }}>
            {/* Icon above the number on a phone, so totals aren't cut to "৳ 131,…". */}
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 1, sm: 1.5 }, alignItems: "flex-start" }}>
                <Box sx={{ bgcolor: `${color}.light`, color: `${color}.dark`, p: 0.75, borderRadius: 1.5, display: "flex", flexShrink: 0 }}>
                    {icon}
                </Box>
                <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
                    <Typography variant="h6" fontWeight={700} noWrap sx={{ fontSize: { xs: "1.05rem", sm: "1.25rem" } }}>{value}</Typography>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                    {sub && <Typography variant="caption" color="text.disabled">{sub}</Typography>}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ── Account card ──────────────────────────────────────────────────────────────

interface AccountCardProps {
    account: FinancialAccountWithBalance;
    totalAssets: number;
    canWrite: boolean;
    onStatement: () => void;
    onEdit: () => void;
    onDelete: () => void;
}

const AccountCard: React.FC<AccountCardProps> = ({ account, totalAssets, canWrite, onStatement, onEdit, onDelete }) => {
    const meta = ACCOUNT_TYPE_META[account.type];
    const isLoan = account.type === "LOAN";
    const balance = Number(account.balance);

    // Non-loan: what share of total cash & bank this one account holds.
    // Loan: how much of the original principal has been repaid — falls
    // out of opening_balance vs the live-computed balance, no new field.
    const sharePct = !isLoan && totalAssets > 0 ? (balance / totalAssets) * 100 : null;
    const opening = Number(account.opening_balance);
    const repaidPct = isLoan && opening > 0 ? ((opening - balance) / opening) * 100 : null;

    return (
        <Card
            variant="outlined"
            sx={{
                height: "100%", display: "flex", flexDirection: "column",
                borderLeft: "4px solid", borderLeftColor: meta.avatarFg,
                transition: "box-shadow 0.15s, transform 0.15s",
                "&:hover": { boxShadow: 3, transform: "translateY(-1px)" },
                opacity: account.is_active ? 1 : 0.65,
            }}
        >
            <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                        <Box sx={{ bgcolor: meta.avatarBg, color: meta.avatarFg, p: 0.75, borderRadius: 1.5, display: "flex", flexShrink: 0 }}>
                            {meta.icon}
                        </Box>
                        <Typography variant="h6" component="h2" noWrap>{account.name}</Typography>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1 }}>
                    <Chip label={meta.label} size="small" color={meta.chipColor} variant="outlined" />
                    {!account.is_active && <Chip label="Inactive" size="small" />}
                    {isLoan && account.interest_rate != null && (
                        <Chip label={`${account.interest_rate}% interest`} size="small" variant="outlined" />
                    )}
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        {isLoan ? "Amount owed" : "Balance"}
                    </Typography>
                    <Typography variant="h5" fontWeight={700} color={isLoan ? "error.main" : "success.main"}>
                        {formatCurrencyBDT(account.balance)}
                    </Typography>
                </Box>

                {isLoan && account.repayment_date && (
                    <Box sx={{ mt: 1 }}>
                        <RepaymentChip date={account.repayment_date} />
                    </Box>
                )}

                {sharePct !== null && (
                    <Box sx={{ mt: 1.5 }}>
                        <LinearProgress
                            variant="determinate"
                            value={Math.min(100, Math.max(0, sharePct))}
                            sx={{ height: 6, borderRadius: 3, bgcolor: "grey.200", "& .MuiLinearProgress-bar": { bgcolor: meta.avatarFg, borderRadius: 3 } }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {sharePct.toFixed(0)}% of cash &amp; bank
                        </Typography>
                    </Box>
                )}

                {repaidPct !== null && (
                    <Box sx={{ mt: 1.5 }}>
                        <LinearProgress
                            variant="determinate"
                            value={Math.min(100, Math.max(0, repaidPct))}
                            sx={{ height: 6, borderRadius: 3, bgcolor: "grey.200", "& .MuiLinearProgress-bar": { bgcolor: "success.main", borderRadius: 3 } }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {repaidPct >= 0 ? `${repaidPct.toFixed(0)}% repaid` : "Borrowed more since opening"}
                        </Typography>
                    </Box>
                )}
            </CardContent>
            <CardActions sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                <Tooltip title="View statement">
                    <IconButton size="small" onClick={onStatement}>
                        <Receipt fontSize="small" />
                    </IconButton>
                </Tooltip>
                {canWrite && (
                    <>
                        <Tooltip title="Edit">
                            <IconButton size="small" onClick={onEdit}>
                                <Edit fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={onDelete}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </CardActions>
        </Card>
    );
};

export const AccountList: React.FC = () => {
    const { canWrite } = usePermissions();
    const [accounts, setAccounts] = useState<FinancialAccountWithBalance[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [formOpen, setFormOpen] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState<FinancialAccount | null>(null);
    const [formMode, setFormMode] = useState<"create" | "edit">("create");
    const [transferOpen, setTransferOpen] = useState(false);
    const [statementAccountId, setStatementAccountId] = useState<string | null>(null);
    const [historyOpen, setHistoryOpen] = useState(false);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            const list = await financialAccountService.listAccounts();
            setAccounts(list);
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || "Failed to load accounts");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const handleSuccess = () => {
        setFormOpen(false);
        setTransferOpen(false);
        invalidateAccountsCache();
        loadData();
    };

    const handleDelete = async (account: FinancialAccount) => {
        if (!window.confirm(`Delete "${account.name}"? This cannot be undone.`)) return;
        try {
            await financialAccountService.deleteAccount(account.account_id);
            invalidateAccountsCache();
            loadData();
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || "Failed to delete account");
        }
    };

    // account.balance/opening_balance arrive as Decimal-serialized JSON
    // strings (e.g. "10000.00"), same as every other money field from this
    // backend — Number(...) here mirrors the coercion formatCurrencyBDT
    // already does internally for single values; raw `+` across multiple
    // values would silently string-concatenate instead of adding.
    const totalAssets = accounts
        .filter((a) => a.type !== "LOAN")
        .reduce((sum, a) => sum + Number(a.balance), 0);
    const totalLoans = accounts
        .filter((a) => a.type === "LOAN")
        .reduce((sum, a) => sum + Number(a.balance), 0);
    const netPosition = totalAssets - totalLoans;

    const assetAccounts = sortAccounts(accounts.filter((a) => a.type !== "LOAN"));
    const loanAccounts = sortAccounts(accounts.filter((a) => a.type === "LOAN"));

    const openCreate = () => { setSelectedAccount(null); setFormMode("create"); setFormOpen(true); };

    const renderCard = (account: FinancialAccountWithBalance) => (
        <Grid key={account.account_id} size={{ xs: 12, sm: 6, md: 4 }}>
            <AccountCard
                account={account}
                totalAssets={totalAssets}
                canWrite={canWrite}
                onStatement={() => setStatementAccountId(account.account_id)}
                onEdit={() => { setSelectedAccount(account); setFormMode("edit"); setFormOpen(true); }}
                onDelete={() => handleDelete(account)}
            />
        </Grid>
    );

    return (
        <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>Accounts</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Where the business's money actually is — cash, bank accounts, and loans.
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button variant="outlined" startIcon={<History />} onClick={() => setHistoryOpen(true)}>
                        History
                    </Button>
                    {canWrite && (
                        <>
                            <Button variant="outlined" startIcon={<SwapHoriz />} onClick={() => setTransferOpen(true)}>
                                Transfer
                            </Button>
                            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
                                Add Account
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

            {accounts.length > 0 && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <KpiCard
                            label="Cash & Bank" value={formatCurrencyBDT(totalAssets)}
                            sub={`${assetAccounts.length} account${assetAccounts.length === 1 ? "" : "s"}`}
                            icon={<WalletOutlined fontSize="small" />} color="success"
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <KpiCard
                            label="Owed on Loans" value={formatCurrencyBDT(totalLoans)}
                            sub={`${loanAccounts.length} loan${loanAccounts.length === 1 ? "" : "s"}`}
                            icon={<CreditCard fontSize="small" />} color="error"
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <KpiCard
                            label="Net Position" value={formatCurrencyBDT(netPosition)}
                            sub="cash & bank minus loans"
                            icon={netPosition >= 0 ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
                            color={netPosition >= 0 ? "primary" : "error"}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <KpiCard
                            label="Accounts" value={String(accounts.length)}
                            sub={`${accounts.filter((a) => a.is_active).length} active`}
                            icon={<AccountBalance fontSize="small" />} color="info"
                        />
                    </Grid>
                </Grid>
            )}

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
            ) : accounts.length === 0 ? (
                <Paper variant="outlined" sx={{ textAlign: "center", py: 6, px: 3 }}>
                    <Box sx={{ display: "inline-flex", bgcolor: "primary.light", color: "primary.dark", p: 1.5, borderRadius: "50%", mb: 1.5 }}>
                        <AccountBalance />
                    </Box>
                    <Typography variant="body1" fontWeight={600}>No accounts yet</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Add your cash drawer, bank accounts, or a loan to start tracking real balances.
                    </Typography>
                    {canWrite && (
                        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
                            Add Your First Account
                        </Button>
                    )}
                </Paper>
            ) : (
                <>
                    {assetAccounts.length > 0 && (
                        <Box sx={{ mb: loanAccounts.length > 0 ? 4 : 0 }}>
                            {loanAccounts.length > 0 && (
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                    Cash &amp; Bank
                                </Typography>
                            )}
                            <Grid container spacing={2}>
                                {assetAccounts.map(renderCard)}
                            </Grid>
                        </Box>
                    )}
                    {loanAccounts.length > 0 && (
                        <Box>
                            {assetAccounts.length > 0 && (
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                    Loans
                                </Typography>
                            )}
                            <Grid container spacing={2}>
                                {loanAccounts.map(renderCard)}
                            </Grid>
                        </Box>
                    )}
                </>
            )}

            <AccountForm
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSuccess={handleSuccess}
                account={selectedAccount}
                mode={formMode}
            />

            <TransferDialog
                open={transferOpen}
                onClose={() => setTransferOpen(false)}
                onSuccess={handleSuccess}
            />

            <AccountStatementDrawer
                open={!!statementAccountId}
                accountId={statementAccountId}
                onClose={() => { setStatementAccountId(null); loadData(); }}
            />

            <TransferHistoryDrawer
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
            />
        </Box>
    );
};
